import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const THRESHOLD_OPTIONS = [
  { value: 'none', label: 'No Alert' },
  { value: 'needs_attention', label: 'Needs Attention & Requires Service' },
  { value: 'requires_service', label: 'Requires Service Only' },
];

interface AlertSettings {
  hvac_overall_visual_condition: string;
  hvac_overall_operation: string;
  hvac_air_filter_condition: string;
  hvac_indoor_air_quality: string;
  plumbing_overall_visual_condition: string;
  plumbing_expansion_tank: string;
  electrical_overall_visual_condition: string;
  electrical_whole_house_surge_protection: string;
  electrical_wiring_secured_properly_terminated: string;
  electrical_whole_home_generator: string;
  water_treatment_visible_condition: string;
}

const DEFAULT_SETTINGS: AlertSettings = {
  hvac_overall_visual_condition: 'none',
  hvac_overall_operation: 'none',
  hvac_air_filter_condition: 'none',
  hvac_indoor_air_quality: 'none',
  plumbing_overall_visual_condition: 'none',
  plumbing_expansion_tank: 'none',
  electrical_overall_visual_condition: 'none',
  electrical_whole_house_surge_protection: 'none',
  electrical_wiring_secured_properly_terminated: 'none',
  electrical_whole_home_generator: 'none',
  water_treatment_visible_condition: 'none',
};

interface SectionConfig {
  title: string;
  attributes: { key: keyof AlertSettings; label: string }[];
}

const SECTIONS: SectionConfig[] = [
  {
    title: 'HVAC',
    attributes: [
      { key: 'hvac_overall_visual_condition', label: 'Overall / Visual Condition' },
      { key: 'hvac_overall_operation', label: 'Overall Operation' },
      { key: 'hvac_air_filter_condition', label: 'Air Filter Condition' },
      { key: 'hvac_indoor_air_quality', label: 'Indoor Air Quality Device' },
    ],
  },
  {
    title: 'Plumbing',
    attributes: [
      { key: 'plumbing_overall_visual_condition', label: 'Overall / Visual Condition' },
      { key: 'plumbing_expansion_tank', label: 'Expansion Tank' },
    ],
  },
  {
    title: 'Electrical',
    attributes: [
      { key: 'electrical_overall_visual_condition', label: 'Overall / Visual Condition' },
      { key: 'electrical_whole_house_surge_protection', label: 'Whole House Surge Protection' },
      { key: 'electrical_wiring_secured_properly_terminated', label: 'Wiring Secured / Properly Terminated' },
      { key: 'electrical_whole_home_generator', label: 'Whole Home Generator' },
    ],
  },
  {
    title: 'Water Treatment',
    attributes: [{ key: 'water_treatment_visible_condition', label: 'Visible Condition of Equipment' }],
  },
];

interface AlertSettingsResponse {
  result: AlertSettings;
}

const sectionHeaderSx = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  bgcolor: '#3d5a80',
  px: 2,
  py: 1,
  borderRadius: '4px 4px 0 0',
};

const AlertNotificationPage: React.FC = () => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const [settings, setSettings] = useState<AlertSettings>(DEFAULT_SETTINGS);

  const { data: response, isLoading, error } = useGet<AlertSettingsResponse>({
    url: '/home_services/inspection_alert_notification_settings',
  });

  const { mutateAsync, isPending } = usePut();

  useEffect(() => {
    const result = response?.data?.result;
    if (result) {
      setSettings({
        hvac_overall_visual_condition: result.hvac_overall_visual_condition ?? 'none',
        hvac_overall_operation: result.hvac_overall_operation ?? 'none',
        hvac_air_filter_condition: result.hvac_air_filter_condition ?? 'none',
        hvac_indoor_air_quality: result.hvac_indoor_air_quality ?? 'none',
        plumbing_overall_visual_condition: result.plumbing_overall_visual_condition ?? 'none',
        plumbing_expansion_tank: result.plumbing_expansion_tank ?? 'none',
        electrical_overall_visual_condition: result.electrical_overall_visual_condition ?? 'none',
        electrical_whole_house_surge_protection: result.electrical_whole_house_surge_protection ?? 'none',
        electrical_wiring_secured_properly_terminated: result.electrical_wiring_secured_properly_terminated ?? 'none',
        electrical_whole_home_generator: result.electrical_whole_home_generator ?? 'none',
        water_treatment_visible_condition: result.water_treatment_visible_condition ?? 'none',
      });
    }
  }, [response]);

  const handleChange = (key: keyof AlertSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/inspection_alert_notification_settings',
        data: { inspection_alert_notification_settings: settings },
      });
      showSuccessSnackBar?.('Alert settings saved.');
    } catch {
      showErrorSnackBar?.('Failed to save alert settings.');
    }
  };

  const loading = isLoading || isPending;
  const errorMessage = error ? 'Failed to load alert settings.' : '';

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={3} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader mainTitle="Home Services" subTitle="Alert Notifications" />
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Configure which inspection report attributes trigger an alert email to Home Services admins when a
            report is submitted.
          </Typography>
        </Grid>

        {SECTIONS.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Paper style={{ overflow: 'hidden' }}>
              <Typography sx={sectionHeaderSx}>{section.title}</Typography>
              <Grid container spacing={2} style={{ padding: 16 }}>
                {section.attributes.map(({ key, label }) => (
                  <Grid item xs={12} key={key}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{label}</InputLabel>
                      <Select
                        value={settings[key]}
                        label={label}
                        onChange={(e) => handleChange(key, e.target.value)}
                      >
                        {THRESHOLD_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            Save Settings
          </Button>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default AlertNotificationPage;
